/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * TypeScript/React Bootstrap v2 version of ActionsMenu
 * Based on the original JSX version in /lego/component/ActionsMenu.jsx
 *
 * Key differences from JSX version:
 * - Uses ServiceContext for translations (consistent with app architecture)
 * - Uses React Bootstrap v2 API (show/onClick instead of open/onSelect)
 * - Uses CSS classes instead of styled-components
 * - Includes local Utils and lodash-style utility functions
 */

import React, { useEffect, useRef, useState } from 'react'
import { useServiceContext } from '../../root'
import Dropdown from 'react-bootstrap/Dropdown'
import styled from 'styled-components'
import Icon from '@mdsol/lego/lib/Icon'

// Utility functions to replace lodash
const _ = {
  indexOf: (array: any[], item: any): number => array.indexOf(item),
  forEach: (array: any[], callback: (item: any, index: number) => void): void => array.forEach(callback),
  extend: (target: any, source: any): any => Object.assign(target, source),
  isFunction: (value: any): value is Function => typeof value === 'function'
}

// Utils replacement for getElementOffset
const Utils = {
  getElementOffset: (element: HTMLElement | null) => {
    if (!element) return { top: 0, left: 0 }
    const rect = element.getBoundingClientRect()
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX
    }
  }
}

interface MenuAction {
  (e?: React.MouseEvent): void
}

interface MenuSubSubItem {
  action?: MenuAction
  label?: string
  disabled?: boolean
  value?: string
  title?: string
}

interface MenuSubItem {
  action?: MenuAction
  label?: string
  disabled?: boolean
  value?: string
  title?: string
  subSubmenu?: MenuSubSubItem[]
}

interface MenuItemData {
  action?: MenuAction
  label?: string
  disabled?: boolean
  value?: string
  title?: string
  submenu?: MenuSubItem[]
  divider?: boolean
}

interface ActionsMenuProps {
  items: MenuItemData[]
  menuId: string
  onSelect?: (eventKey: string) => void
  isBigMenu?: boolean
  onToggle?: (isOpen: boolean) => void
}

interface ActionSubMenuProps {
  label?: string
  submenu?: MenuSubItem[]
  closeParentFn?: () => void
  submenuToTop: boolean
}

interface ActionSubSubMenuProps {
  label?: string
  subSubmenu?: MenuSubSubItem[]
  closeParentFn?: () => void
  submenuToTop?: boolean
}

export const StyledActionMenu = styled.div`
  .btn-group,
  .btn-group.show,
  .btn-group.dropdown {
    position: relative !important;
    display: inline-block !important;
    vertical-align: middle !important;
  }

  .dropdown-toggle,
  .dropdown-toggle.show,
  button.dropdown-toggle {
    position: relative !important;
    outline: none !important;
    box-shadow: none !important;

    &:focus,
    &:active,
    &.show,
    &:hover {
      outline: none !important;
      box-shadow: none !important;
      transform: none !important;
    }

    &::after {
      display: none !important;
    }
  }

  ul.dropdown-menu {
    z-index: 999;
    min-width: 200px;
    padding: 5px 0;

    li {
      &.divider {
        margin: 4px 0;
      }
      a.disabled {
        color: #898989 !important;
      }
      a {
        word-break: normal;
        word-wrap: break-word;
        font-size: 12px;
        color: #3374c8;

        &:hover {
          background-color: #f5f5f5;
          color: #3374c8;
          text-decoration: none !important;
        }
      }
    }
  }
`

const ActionsMenu: React.FC<ActionsMenuProps> = ({ items, menuId, onSelect, isBigMenu, onToggle }) => {
  const {
    service: {
      filter: { translate }
    }
  } = useServiceContext()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isMultiLevel, setIsMultiLevel] = useState<boolean>(false)
  const [options, setOptions] = useState<MenuItemData[]>([])
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownMenuRef = useRef<HTMLElement>(null)
  const [menuTop, setMenuTop] = useState<boolean>(false)

  const closeMenu = () => {
    setIsOpen(false)
    // Manually notify parent of menu close. Since our Dropdown.Items lack eventKeys,
    // they bypass react-bootstrap's autoClose onToggle. Without this, parent hooks
    // (e.g., useFsiActionMenu) get stuck with isOpen=true, causing unintended
    // deselectAll() and stale API calls during subsequent row checkbox interactions.
    if (_.isFunction(onToggle)) {
      onToggle(false)
    }
  }

  const toggleCssOfDropdownMenuAfterPositionCalculation = (newToggleState: boolean) => {
    const el = dropdownMenuRef.current
    if (el) {
      el.style.display = newToggleState ? 'block' : 'none'
      el.style.visibility = newToggleState ? 'visible' : 'hidden'
    }
  }

  const calculateDropdownMenuPosition = () => {
    const el = dropdownMenuRef.current
    if (!el) return

    // the element is hidden => making the el block, so we can measure its height but still be hidden
    el.style.visibility = 'hidden'
    el.style.display = 'block'
    const menuHeight = el.offsetHeight
    // reverting to the original values
    toggleCssOfDropdownMenuAfterPositionCalculation(isOpen)

    const margin = 20
    const buttonTopOffset = Utils.getElementOffset(dropdownRef.current).top
    const buttonHeight = dropdownRef.current?.offsetHeight || 0
    const windowHeight = document.body.scrollHeight
    const menuToTop = windowHeight - (buttonTopOffset + buttonHeight) <= menuHeight + margin

    setMenuTop(menuToTop)
  }

  const isSubmenuToTop = (menuItem: MenuItemData) => {
    const itemIndex = _.indexOf(options, menuItem)
    const menuLength = options.length
    const itemsBelow = menuLength - (itemIndex + 1)
    const subMenuLength = menuItem.submenu?.length || 0
    return subMenuLength > itemsBelow
  }

  useEffect(() => {
    if (dropdownRef.current) {
      dropdownMenuRef.current = dropdownRef.current.querySelector('.dropdown-menu')
    }
  }, [])

  useEffect(() => {
    const opts: MenuItemData[] = []

    _.forEach(items, (item) => {
      opts.push({
        ...item,
        label: item.label || (item.value ? translate(item.value) : '') || item.title || ''
      })
      if (item.submenu && Array.isArray(item.submenu)) {
        _.forEach(item.submenu, (subItem) => {
          if (subItem.subSubmenu && Array.isArray(subItem.subSubmenu)) {
            _.forEach(subItem.subSubmenu, (subSubItem) => {
              _.extend(subSubItem, {
                label:
                  subSubItem.label || (subSubItem.value ? translate(subSubItem.value) : '') || subSubItem.title || ''
              })
            })
            setIsMultiLevel(true)
          }
          _.extend(subItem, {
            label: subItem.label || (subItem.value ? translate(subItem.value) : '') || subItem.title || ''
          })
        })

        setIsMultiLevel(true)
      }
    })

    setOptions(opts)
  }, [items, translate])

  useEffect(() => {
    calculateDropdownMenuPosition()
  }, [options, isOpen])

  return (
    <StyledActionMenu ref={dropdownRef}>
      <Dropdown
        className="btn-group"
        show={isOpen}
        onToggle={(isOpen: boolean) => {
          setIsOpen(isOpen)
          if (_.isFunction(onToggle)) {
            onToggle(isOpen)
          }
          toggleCssOfDropdownMenuAfterPositionCalculation(isOpen)
        }}
        drop={menuTop ? 'up' : 'down'}
        align="end"
      >
        {isBigMenu ? (
          <Dropdown.Toggle id={`action-menu-${menuId}`} className="btn-actions" variant="light">
            <span className="mr5">{translate('ACTIONS_MENU_TITLE')}</span>
            <span className="caret"></span>
          </Dropdown.Toggle>
        ) : (
          <Dropdown.Toggle id={`action-menu-${menuId}`} variant="default" className="p-0" style={{ height: '24px' }}>
            <Icon name="more-menu" className="icon-small" />
          </Dropdown.Toggle>
        )}

        <Dropdown.Menu as={'ul'} className={isMultiLevel ? 'multi-level' : ''}>
          {options.map((item, index) => {
            const { action, label, disabled, value, submenu, divider } = item
            const itemKey = value || `item-${index}`
            return !submenu ? (
              divider ? (
                <Dropdown.Divider style={{ margin: '3px' }} key={'divider_' + index} />
              ) : (
                <li key={itemKey}>
                  <Dropdown.Item
                    id={`action-menu-${menuId}-option-${itemKey}`}
                    key={itemKey}
                    disabled={disabled}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      closeMenu()
                      if (onSelect) {
                        onSelect(itemKey)
                      } else {
                        action?.(e)
                      }
                    }}
                  >
                    {label}
                  </Dropdown.Item>
                </li>
              )
            ) : (
              <ActionSubMenu
                {...item}
                submenuToTop={isSubmenuToTop(item)}
                key={'submenu_' + itemKey}
                closeParentFn={closeMenu}
              />
            )
          })}
        </Dropdown.Menu>
      </Dropdown>
    </StyledActionMenu>
  )
}

export const ActionSubMenu: React.FC<ActionSubMenuProps> = ({ label, submenu, closeParentFn, submenuToTop }) => {
  return (
    <li className="dropdown-submenu">
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault()
        }}
      >
        {label}
      </a>
      <ul className={'dropdown-menu' + (submenuToTop ? ' dropup' : '')}>
        {submenu?.map(({ action, label, disabled, value, subSubmenu }, index) => {
          const itemKey = value || `subitem-${index}`
          return !subSubmenu ? (
            <li>
              <Dropdown.Item
                id={`action-menu--option-${itemKey}`}
                key={itemKey}
                disabled={disabled}
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  action?.(e)
                  closeParentFn?.()
                }}
              >
                {label}
              </Dropdown.Item>
            </li>
          ) : (
            <ActionSubSubMenu
              {...{ label, disabled, value, subSubmenu }}
              submenuToTop={false}
              key={'submenu_' + itemKey}
              closeParentFn={closeParentFn}
            />
          )
        })}
      </ul>
    </li>
  )
}
export const ActionSubSubMenu: React.FC<ActionSubSubMenuProps> = ({
  label,
  subSubmenu,
  closeParentFn,
  submenuToTop
}) => {
  return (
    <li className="dropdown-submenu">
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault()
        }}
      >
        {label}
      </a>
      <ul className={'dropdown-menu' + (submenuToTop ? ' dropup' : '')}>
        {subSubmenu?.map(({ action, label, disabled, value }, index) => {
          const itemKey = value || `subsubitem-${index}`
          return (
            <li key={itemKey}>
              <Dropdown.Item
                id={`action-submenu-option-${itemKey}`}
                key={itemKey}
                disabled={disabled}
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  action?.(e)
                  closeParentFn?.()
                }}
              >
                {label}
              </Dropdown.Item>
            </li>
          )
        })}
      </ul>
    </li>
  )
}

export default ActionsMenu

/* doc:
https://github.com/mdsol/lego/blob/develop/packages/lego/src/QuickMenu/QuickMenu.js
*/
